import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import { selectors as quoteCreateSelectors } from 'modules/api/market/cart/admin/quote/createModule';
import { cartDetailsSelectors } from 'modules/api/market/cart/detailsModule';
import { cartExpireSelectors } from 'modules/api/market/cart/expireModule';
import { selectors as primaryCategoryMappingSelectors } from 'modules/api/product/primaryCategoryMappingModule';
import appConfigSelectors from 'modules/appConfig/selectors';
import { isBasketAdmin as getIsBasketAdmin } from 'modules/auth';

import { categoryMap } from 'modules/primaryCategoryMapping';
import { createSelector } from 'reselect';

const getStateSlice = (state) => state.cart;

const getCartIsInitialized = createSelector(
	getStateSlice,
	(slice) => slice.cartInitialized,
);

const getCartInitHasStarted = createSelector(
	getStateSlice,
	(slice) => slice.cartInitHasStarted,
);

const isCustomerPreview = createSelector(
	getStateSlice,
	(slice) => slice.isCustomerPreview,
);

const getHasStormItems = createSelector(
	cartDetailsSelectors.getNativeItems,
	primaryCategoryMappingSelectors.getNativeData,
	(items, catMap) =>
		Boolean(items.find((item) => categoryMap[catMap?.[item.code]] === 'cloud')),
);

const getHasDedicatedItems = createSelector(
	cartDetailsSelectors.getNativeItems,
	primaryCategoryMappingSelectors.getNativeData,
	(items, catMap) =>
		Boolean(
			items.find((item) => categoryMap[catMap?.[item.code]] === 'dedicated'),
		),
);

const getIsCartLocked = createSelector(
	getIsBasketAdmin,
	cartDetailsSelectors.getIsQuote,
	cartDetailsSelectors.getIsOrderable,
	cartDetailsSelectors.getAdminData,
	(isBasketAdmin, isQuote, isOrderable, adminData) => {
		if (!isBasketAdmin) {
			return isQuote;
		}

		return fromApiBoolean(adminData.locked) || (isQuote && isOrderable);
	},
);

const getDisplayAdminUI = createSelector(
	getIsBasketAdmin,
	isCustomerPreview,
	(isBasketAdmin, isPreview) => !isPreview && isBasketAdmin,
);

const getExpireIsLoading = createSelector(
	cartExpireSelectors.isLoading,
	cartDetailsSelectors.hasData,
	(expireLoading, basketHasData) => expireLoading || !basketHasData,
);

const getIsCartBadgeLoading = createSelector(
	getCartIsInitialized,
	quoteCreateSelectors.isLoading,
	(cartHasInit, quoteCreateIsLoading) => !cartHasInit || quoteCreateIsLoading,
);

const getCartNotReady = createSelector(
	getIsBasketAdmin,
	cartDetailsSelectors.getNativeData,
	(isBasketAdmin, cartData) => !isBasketAdmin && !cartData?.orderable,
);

const getCartNameDisplay = createSelector(
	getIsBasketAdmin,
	cartDetailsSelectors.getMetadata,
	(isBasketAdmin, metadata) => {
		const nameRaw = metadata.name;

		if (isBasketAdmin && !nameRaw) {
			return 'Unnamed Cart';
		}

		return nameRaw || 'My Cart';
	},
);

const getCartTypeToolTip = createSelector(
	cartDetailsSelectors.getIsQuote,
	getIsBasketAdmin,
	(isQuote, isBasketAdmin) => {
		if (!isBasketAdmin) {
			return '';
		}

		if (isQuote) {
			return 'Quote';
		}

		return 'Basic Cart';
	},
);

const getSalesforceQuoteUrl = createSelector(
	cartDetailsSelectors.getQuoteId,
	appConfigSelectors.getSalesforceHostname,
	(quoteId, salesforceHostname) =>
		quoteId ? `${salesforceHostname}/Quote/${quoteId}/view` : '',
);

const selectors = {
	getCartInitHasStarted,
	getCartIsInitialized,
	getCartNameDisplay,
	getCartNotReady,
	getCartTypeToolTip,
	getDisplayAdminUI,
	getExpireIsLoading,
	getHasDedicatedItems,
	getHasStormItems,
	getIsCartBadgeLoading,
	getIsCartLocked,
	getSalesforceQuoteUrl,
	isCustomerPreview,
};

export default selectors;
